/**
 * How big the creature actually is — one authority for the mesh that is drawn and the
 * volume that is shot at.
 *
 * The Kraken and its limbs were authored twice. The mesh builder gave the animal a
 * kilometre of mantle and limbs standing sixty metres out of the sea; the vessel specs
 * still carried the small placeholder hull it was prototyped from — a 120 m creature and
 * a 12 m limb. Everything the simulation does with a hull's size therefore ran against a
 * body that had not been drawn for a long time: the tentacle's hit volume covered the
 * bottom fifth of a limb the player could see all of, and the Kraken collided as
 * something a tenth of its own silhouette.
 *
 * Adam, on finding it: "the spec is wrong — it initially made the Kraken very small…
 * cement the ones that make the Kraken monumentally huge." So the render is the truth and
 * this module is where it is stated. `content/vessels.ts` takes its creature dimensions
 * from here, and `render/ships/vesselMeshes.ts` builds from the same numbers and the same
 * authored curve. Neither is allowed a second opinion.
 *
 * Data and pure geometry only — this is imported by `sim/` through the vessel specs, so
 * it must never reach for an engine.
 *
 * Axes match every other vessel: local +Z is the bow/reach, +X is starboard, Y=0 is the
 * waterline, metres throughout.
 */

import { TENTACLE_MAX_EXCURSION } from './tentacleMotion';

/** Full surfaced extent of the animal. The mantle alone is most of it. */
export const KRAKEN_VISUAL_LENGTH = 1000;
export const KRAKEN_VISUAL_BEAM = 540;
export const KRAKEN_VISUAL_HEIGHT = 310;

/** The rendered limb's reach above and around its root. */
export const KRAKEN_TENTACLE_VISUAL_HEIGHT = 104;

export interface CreaturePoint {
  x: number;
  y: number;
  z: number;
}

/**
 * The authored tentacle centreline, from root (t=0) to tip (t=1).
 *
 * The root sits well below the mean surface so the tube's end cap never appears as a cut
 * hose between troughs; foam and spray reveal the displaced volume while the sea keeps the
 * anatomical join hidden. The limb leans out as it rises, which is why a plain vertical
 * column is not a description of it.
 */
export function tentaclePathPoint(t: number): CreaturePoint {
  const x = Math.sin(t * Math.PI * 2.25) * (3 + 18 * Math.pow(t, 1.40));
  const z = Math.sin(t * Math.PI * 0.86) * 34
    + t * 8
    + Math.sin(t * Math.PI * 3.1) * 2.4 * t;
  const y = -30
    + t * (KRAKEN_TENTACLE_VISUAL_HEIGHT + 16)
    + Math.sin(t * Math.PI) * 13
    - Math.pow(t, 3) * 26;
  return { x, y, z };
}

/** Flesh radius at a point along the limb: thick at the root, tapering to the hook. */
export function tentacleRadius(t: number): number {
  return 14 * Math.pow(1 - t, 0.72) + 2.1 * t;
}

/** A sphere of the hit volume, in the body's own local frame. */
export interface HitSphere {
  x: number;
  y: number;
  z: number;
  r: number;
  /** Progress along the body this sphere sits at, 0 at the root and 1 at the tip. */
  t: number;
}

/**
 * The limb's hit volume: spheres strung along the authored curve.
 *
 * Adam: "they only take damage when you shoot them in one particular spot, which seems a
 * tad unrealistic." He was describing the hit volume exactly. Every hull is modelled as a
 * capsule around its centreline — right for something long and floating, wrong for a limb
 * that stands sixty metres up and leans forty metres out. Following the same curve the
 * mesh is swept along is the only description that makes the whole visible limb shootable.
 *
 * Spacing is derived from the local radius so consecutive spheres always overlap: the
 * column has no gaps at the thick root or the thin hook. A floor under the tip radius
 * keeps the last few metres a target a player can actually hit at combat range without
 * inflating the rest of the limb.
 */
function buildTentacleHitSpheres(): readonly HitSphere[] {
  const spheres: HitSphere[] = [];
  const TIP_FLOOR = 3.2;
  let last: HitSphere | null = null;
  for (let i = 0; i <= 400; i++) {
    const t = i / 400;
    const p = tentaclePathPoint(t);
    const r = Math.max(tentacleRadius(t), TIP_FLOOR);
    if (last) {
      const gap = Math.hypot(p.x - last.x, p.y - last.y, p.z - last.z);
      // 0.8 of the smaller radius: neighbours overlap even where the limb is thinnest.
      if (gap < Math.min(last.r, r) * 0.8 && i < 400) continue;
    }
    const sphere = { x: p.x, y: p.y, z: p.z, r, t };
    spheres.push(sphere);
    last = sphere;
  }
  return spheres;
}

export const TENTACLE_HIT_SPHERES: readonly HitSphere[] = buildTentacleHitSpheres();

function measureTentacle(): { length: number; beam: number; height: number; draft: number } {
  let minX = Infinity; let maxX = -Infinity;
  let minY = Infinity; let maxY = -Infinity;
  let minZ = Infinity; let maxZ = -Infinity;
  for (let i = 0; i <= 400; i++) {
    const t = i / 400;
    const p = tentaclePathPoint(t);
    const r = tentacleRadius(t);
    minX = Math.min(minX, p.x - r); maxX = Math.max(maxX, p.x + r);
    minY = Math.min(minY, p.y - r); maxY = Math.max(maxY, p.y + r);
    minZ = Math.min(minZ, p.z - r); maxZ = Math.max(maxZ, p.z + r);
  }
  return {
    length: maxZ - minZ,
    beam: maxX - minX,
    // `height` is what stands above the waterline — the part a gun can see.
    height: maxY,
    // `draft` is what the sea hides. A killed limb withdraws through all of it.
    draft: -minY,
  };
}

/**
 * The rendered limb's measured envelope, rounded to the metre.
 *
 * Measured rather than authored: the numbers below are what the curve and its taper
 * actually occupy, so they cannot drift away from the mesh when the curve is retuned.
 */
export const TENTACLE_ENVELOPE = measureTentacle();

/**
 * The broad-phase radius that encloses the whole limb from its root position.
 *
 * The narrow phase walks ~40 spheres; this rejects a limb the round was never near
 * before any of that runs.
 */
export const TENTACLE_BOUND_RADIUS = Math.max(
  TENTACLE_HIT_SPHERES.reduce(
    (worst, s) => Math.max(worst, Math.hypot(s.x, s.y, s.z) + s.r),
    0,
  ) + TENTACLE_MAX_EXCURSION,
  /*
   * A gripping limb leaves its own curve entirely: a ship wrap reaches the far rail of
   * a hull up to 145 m off, and the rig wrap reaches from a root 60-88 m out onto the
   * deck edge at 40. The broad phase must cover the wrapped pose, not just the authored
   * one, or rounds into a coiled limb are rejected before the narrow phase ever looks.
   */
  190,
);

// --- The creature's eye, and the rise that brings it level with the deck --------------

/**
 * The eye's resting position in the Kraken's local frame, matching the mesh: one
 * enormous lateral lens on the starboard cheek (`buildKraken`, eyeSide = 1, at
 * beam * 0.635 with the anatomy beam of 440).
 */
export const KRAKEN_EYE_LOCAL = { x: 279.4, y: 34, z: 210 } as const;

/** How far the head pitches back at full rise, radians, and the small body lift with it. */
export const KRAKEN_RISE_PITCH = 0.34;
export const KRAKEN_RISE_LIFT = 8;

function smoothstepScale(value: number): number {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
}

/** The instance pose the renderer applies during the rise: pitch about local X, lift. */
export function krakenRisePose(progress: number): { pitch: number; lift: number } {
  const s = smoothstepScale(progress);
  return { pitch: KRAKEN_RISE_PITCH * s, lift: KRAKEN_RISE_LIFT * s };
}

/**
 * Where the eye is in the world at a given rise progress.
 *
 * This is the aiming contract of the finale: the renderer tilts the head so the eye sits
 * exactly here, and the simulation counts shell strikes against exactly here. The pitch
 * math mirrors the render transform (pitch about local X, then yaw): with the eye 210 m
 * forward of the body centre, 0.34 rad of head-back carries it from 34 m up to just over
 * the deck line — the creature does not surface further, it looks up at you.
 */
export function krakenEyePoint(
  bossX: number,
  bossY: number,
  bossZ: number,
  heading: number,
  riseProgress: number,
): CreaturePoint {
  const { pitch, lift } = krakenRisePose(riseProgress);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const ex = KRAKEN_EYE_LOCAL.x;
  const ey = KRAKEN_EYE_LOCAL.y * cp + KRAKEN_EYE_LOCAL.z * sp;
  const ez = KRAKEN_EYE_LOCAL.z * cp - KRAKEN_EYE_LOCAL.y * sp;
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  return {
    x: bossX + ex * c + ez * s,
    y: bossY + ey + lift,
    z: bossZ - ex * s + ez * c,
  };
}

/**
 * The heading that presents the eye to the platform.
 *
 * Solves R(heading) * (eye local xz) = -u * |eye local xz|, where u points from the
 * origin toward the body — i.e. the eye sits on the line between the creature and the
 * rig, on the creature's near side, looking in.
 */
export function krakenStareHeading(bossX: number, bossZ: number): number {
  const pitch = KRAKEN_RISE_PITCH;
  const ex = KRAKEN_EYE_LOCAL.x;
  const ez = KRAKEN_EYE_LOCAL.z * Math.cos(pitch) - KRAKEN_EYE_LOCAL.y * Math.sin(pitch);
  const range = Math.max(1, Math.hypot(bossX, bossZ));
  // Local eye direction vs the direction we want it to point (back toward the origin).
  const localAngle = Math.atan2(ez, ex);
  const wantAngle = Math.atan2(-bossZ / range, -bossX / range);
  // World = Rot(-heading) . local in the (x, z) plane, so heading = local - want.
  return localAngle - wantAngle;
}

/** Horizontal distance from body centre to the eye at full rise — sets the stare range. */
export const KRAKEN_EYE_REACH = Math.hypot(
  KRAKEN_EYE_LOCAL.x,
  KRAKEN_EYE_LOCAL.z * Math.cos(KRAKEN_RISE_PITCH)
    - KRAKEN_EYE_LOCAL.y * Math.sin(KRAKEN_RISE_PITCH),
);
